import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  BLEND_MAX_CONCEPTS,
  BLEND_SCALE_VALUES,
} from "domains/models/constants/blend";
import useRouter from "domains/navigation/hooks/useRouter";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  useDeleteModelsByModelIdMutation,
  useLazyGetModelsByModelIdQuery,
  usePostModelsMutation,
  usePutModelsByModelIdMutation,
  usePutModelsTrainByModelIdMutation,
} from "infra/api/generated/api";
import _ from "lodash";

type BaseModel = "sd-xl" | "flux.1";

interface CreateBlendProviderProps {
  name: string;
  setName: (name: string) => void;

  concepts: NonNullable<GetModelsByModelIdApiResponse["model"]["concepts"]>;
  addConcept: (modelId: string) => void;
  removeConcept: (modelId: string) => void;
  setConcepts: (
    concepts: NonNullable<GetModelsByModelIdApiResponse["model"]["concepts"]>
  ) => void;

  prompt: string;
  setPrompt: (prompt: string) => void;
  negativePrompt: string;
  setNegativePrompt: (prompt: string) => void;

  isTryOutModalOpen: boolean;
  setIsTryOutModalOpen: (isTryOutModalOpen: boolean) => void;
  isSaveLoading: boolean;
  setIsSaveLoading: (isSaveLoading: boolean) => void;
  isGenerateLoading: boolean;
  setIsGenerateLoading: (isTestLoading: boolean) => void;
  getModel: () => Promise<GetModelsByModelIdApiResponse["model"] | undefined>;
  isSaveDisabled: boolean;
  isGenerateDisabled: boolean;
  search: string;
  setSearch: (search: string) => void;

  type: BaseModel;
  setType: (type: BaseModel) => void;
}

export const CreateBlendContext = createContext<CreateBlendProviderProps>({
  name: "",
  setName: () => {},
  concepts: [],
  addConcept: () => {},
  removeConcept: () => {},
  setConcepts: () => {},
  prompt: "",
  setPrompt: () => {},
  negativePrompt: "",
  setNegativePrompt: () => {},
  isTryOutModalOpen: false,
  setIsTryOutModalOpen: () => {},
  isSaveLoading: false,
  setIsSaveLoading: () => {},
  isGenerateLoading: false,
  setIsGenerateLoading: () => {},
  getModel: async () => undefined,
  isSaveDisabled: false,
  isGenerateDisabled: false,
  search: "",
  setSearch: () => {},
  type: "sd-xl",
  setType: () => {},
});

export function CreateBlendProvider({
  children = <></>,
}: {
  children?: React.ReactNode;
}) {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const handleApiError = useHandleApiError();
  const { infoToast } = useScenarioToast();
  const { showLimitModal } = usePlanContext();

  const [search, setSearch] = useState<string>("");
  const [type, setType] = useState<BaseModel>("flux.1");

  const [model, setModel] = useState<
    GetModelsByModelIdApiResponse["model"] | undefined
  >();

  const [concepts, setConcepts] = useState<
    NonNullable<GetModelsByModelIdApiResponse["model"]["concepts"]>
  >([]);

  const setModelAndUrl = useCallback(
    async (model: GetModelsByModelIdApiResponse["model"]) => {
      setModel(model);
      if (router.query.modelId !== model.id) {
        void router.push({
          pathname: router.pathname,
          query: {
            ...router.query,
            modelId: model.id,
          },
        });
      }
    },
    [router]
  );

  const [isTryOutModalOpen, setIsTryOutModalOpen] = useState<boolean>(false);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const [isGenerateLoading, setIsGenerateLoading] = useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>("");
  const [negativePrompt, setNegativePrompt] = useState<string>("");

  const [createModelTrigger] = usePostModelsMutation();
  const [trainModelTrigger] = usePutModelsTrainByModelIdMutation();

  const [name, setName] = useState<string>("New Model");

  const createAndTrainModel = useCallback(async (): Promise<
    GetModelsByModelIdApiResponse["model"] | undefined
  > => {
    if (!name || !concepts.length) {
      return;
    }
    try {
      const { model } = await createModelTrigger({
        projectId: selectedProject.id,
        body: {
          name,
          concepts,
          type: type === "sd-xl" ? "sd-xl-composition" : "flux.1-composition",
        } as any,
      }).unwrap();

      try {
        await trainModelTrigger({
          projectId: selectedProject.id,
          modelId: model.id,
          body: {},
        }).unwrap();
        return model;
      } catch (error: any) {
        handleApiError(error, "There was an error starting the training", {
          quota: () => {
            const reason = _.get(error, "data.reason");
            if (reason.includes("minute")) {
              showLimitModal("planModelMinuteTrainingRateLimit");
            } else if (reason.includes("hourly")) {
              showLimitModal("planModelHourlyTrainingRateLimit");
            } else if (reason.includes("daily")) {
              showLimitModal("planModelDailyTrainingRateLimit");
            }
          },
        });
      }
    } catch (error) {
      handleApiError(error, "There was an error creating the model");
    }
  }, [
    type,
    name,
    createModelTrigger,
    selectedProject.id,
    concepts,
    trainModelTrigger,
    handleApiError,
    showLimitModal,
  ]);

  const [deleteModelTrigger] = useDeleteModelsByModelIdMutation();
  const [updateModelTrigger] = usePutModelsByModelIdMutation();
  const getModel = useCallback(async (): Promise<
    GetModelsByModelIdApiResponse["model"] | undefined
  > => {
    if (
      model &&
      _.isEqual(
        (model.concepts ?? []).map((concept) => concept.modelId).sort(),
        concepts.map((concept) => concept.modelId).sort()
      )
    ) {
      const areConceptsAndScalesEquals = _.isEqual(
        [...(model.concepts ?? [])].sort((a, b) =>
          a.modelId > b.modelId ? 1 : -1
        ),
        [...concepts].sort((a, b) => (a.modelId > b.modelId ? 1 : -1))
      );
      if (areConceptsAndScalesEquals && model.name === name) {
        return model;
      }
      try {
        const { model: updatedModel } = await updateModelTrigger({
          projectId: selectedProject.id,
          modelId: model.id,
          body: {
            concepts: areConceptsAndScalesEquals ? undefined : concepts,
            name,
          },
        }).unwrap();
        await setModelAndUrl(updatedModel);
        return updatedModel;
      } catch (error) {
        handleApiError(error, "There was an error saving the settings");
        return;
      }
    }

    if (model) {
      try {
        await deleteModelTrigger({
          projectId: selectedProject.id,
          modelId: model.id,
        }).unwrap();
      } catch (error) {
        handleApiError(error, "There was an error deleting the model");
        return;
      }
    }

    const newModel = await createAndTrainModel();
    if (newModel) {
      await setModelAndUrl(newModel);
      return newModel;
    }
  }, [
    model,
    concepts,
    createAndTrainModel,
    name,
    updateModelTrigger,
    selectedProject.id,
    setModelAndUrl,
    handleApiError,
    deleteModelTrigger,
  ]);

  const [isRouterReadyHandled, setIsRouterReadyHandled] =
    useState<boolean>(false);
  const [getModelTrigger, { isLoading: isLoadingModel }] =
    useLazyGetModelsByModelIdQuery();

  const areButtonsDisabled = useMemo(() => {
    return (
      !name ||
      !concepts.length ||
      isGenerateLoading ||
      isLoadingModel ||
      !isRouterReadyHandled
    );
  }, [name, concepts, isGenerateLoading, isLoadingModel, isRouterReadyHandled]);

  function getNewScaleValue(modelsCount: number) {
    return (
      BLEND_SCALE_VALUES[modelsCount] ??
      BLEND_SCALE_VALUES[BLEND_SCALE_VALUES.length - 1]
    );
  }

  const addConcept = useCallback(
    (modelId: string) => {
      setConcepts((currentConcepts) => {
        if (currentConcepts.some((concept) => concept.modelId === modelId)) {
          return currentConcepts;
        }

        if (currentConcepts.length >= BLEND_MAX_CONCEPTS) {
          infoToast({
            title: "You can only add up to 5 models to a composition",
            description: "Please remove a model before adding another one",
          });
          return currentConcepts;
        }

        const newScaleValue = getNewScaleValue(currentConcepts.length + 1);
        const newModel = { modelId: modelId, scale: newScaleValue };

        return [...currentConcepts, newModel].map((model) => {
          return { ...model, scale: Math.min(newScaleValue, model.scale) };
        });
      });
    },
    [infoToast]
  );

  const removeConcept = useCallback(
    (modelId: string) =>
      setConcepts((currentConcepts) =>
        currentConcepts.filter((concept) => concept.modelId !== modelId)
      ),
    [setConcepts]
  );

  useEffect(() => {
    void (async () => {
      if (!router.isReady || isRouterReadyHandled) return;
      setIsRouterReadyHandled(true);
      const conceptToAdd = router.query.conceptToAdd as string;
      const modelId = router.query.modelId as string;

      if (conceptToAdd) {
        addConcept(conceptToAdd);
        void router.replace({
          pathname: router.pathname,
          query: _.omit(router.query, "conceptToAdd"),
        });
      }

      if (!modelId) {
        return;
      }

      const { data } = await getModelTrigger({
        modelId,
        projectId: selectedProject.id,
      });
      if (data?.model) {
        setName(data.model.name ?? "New Model");
        setConcepts(data.model.concepts ?? []);
      }
    })();
  }, [
    router.isReady,
    isRouterReadyHandled,
    getModelTrigger,
    selectedProject.id,
    router.query.modelId,
    router,
    addConcept,
  ]);

  const firstConceptModelId = concepts[0]?.modelId;
  useEffect(() => {
    void (async () => {
      if (firstConceptModelId) {
        const { data } = await getModelTrigger({
          modelId: firstConceptModelId,
          projectId: selectedProject.id,
        });
        if (data?.model) {
          setType(data.model.type === "sd-xl-lora" ? "sd-xl" : "flux.1");
        }
      }
    })();
  }, [firstConceptModelId, getModelTrigger, selectedProject.id]);

  const createBlendContextValue = useMemo(
    () => ({
      name,
      setName,
      concepts,
      addConcept,
      removeConcept,
      setConcepts,
      isTryOutModalOpen,
      setIsTryOutModalOpen,
      isSaveLoading,
      setIsSaveLoading,
      isGenerateLoading,
      setIsGenerateLoading,
      getModel,
      isSaveDisabled: areButtonsDisabled || isGenerateLoading,
      isGenerateDisabled: areButtonsDisabled || isSaveLoading,
      prompt,
      setPrompt,
      negativePrompt,
      setNegativePrompt,
      search,
      setSearch,
      type,
      setType,
    }),
    [
      name,
      concepts,
      addConcept,
      removeConcept,
      setConcepts,
      isTryOutModalOpen,
      isSaveLoading,
      isGenerateLoading,
      getModel,
      areButtonsDisabled,
      prompt,
      negativePrompt,
      search,
      type,
      setType,
    ]
  );

  return (
    <CreateBlendContext.Provider value={createBlendContextValue}>
      {children}
    </CreateBlendContext.Provider>
  );
}

export function useCreateBlendContext() {
  return useContext<CreateBlendProviderProps>(CreateBlendContext);
}
